import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  FormErrorMessage,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useEmail } from '../../hooks/useEmail';

export function EmailForm() {
  const { sendEmail, loading, error, clearError } = useEmail();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [contentType, setContentType] = useState<'html' | 'text'>('html');
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!to.trim()) {
      errors.to = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
      errors.to = 'Invalid email address';
    }

    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (contentType === 'html' && !html.trim()) {
      errors.content = 'HTML content is required';
    } else if (contentType === 'text' && !text.trim()) {
      errors.content = 'Text content is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const parseEmails = (value: string): string[] => {
    return value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    clearError();

    if (!validateForm()) {
      return;
    }

    const emailData = {
      to: to.trim(),
      subject: subject.trim(),
      ...(contentType === 'html' ? { html: html.trim() } : { text: text.trim() }),
      ...(cc.trim() && { cc: parseEmails(cc) }),
      ...(bcc.trim() && { bcc: parseEmails(bcc) }),
    };

    const result = await sendEmail(emailData);

    if (result?.success) {
      setSuccess(`Email sent successfully! ID: ${result.id}`);
      setTo('');
      setSubject('');
      setHtml('');
      setText('');
      setCc('');
      setBcc('');
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {success && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Box>
            <CloseButton
              position="relative"
              right={-1}
              top={-1}
              onClick={() => setSuccess(null)}
            />
          </Alert>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
            <CloseButton
              position="relative"
              right={-1}
              top={-1}
              onClick={clearError}
            />
          </Alert>
        )}

        <FormControl isRequired isInvalid={!!validationErrors.to}>
          <FormLabel>To</FormLabel>
          <Input
            type="email"
            placeholder="recipient@example.com"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <FormErrorMessage>{validationErrors.to}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!validationErrors.subject}>
          <FormLabel>Subject</FormLabel>
          <Input
            placeholder="Email subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <FormErrorMessage>{validationErrors.subject}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!validationErrors.content}>
          <FormLabel>Content</FormLabel>
          <Tabs
            variant="soft-rounded"
            colorScheme="blue"
            size="sm"
            onChange={(index) => setContentType(index === 0 ? 'html' : 'text')}
          >
            <TabList mb={2}>
              <Tab>HTML</Tab>
              <Tab>Plain Text</Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <Textarea
                  placeholder="Enter your HTML content here..."
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  minH="150px"
                  fontFamily="mono"
                />
              </TabPanel>
              <TabPanel p={0}>
                <Textarea
                  placeholder="Enter your plain text content here..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  minH="150px"
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
          <FormErrorMessage>{validationErrors.content}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>CC (comma-separated)</FormLabel>
          <Input
            placeholder="cc1@example.com, cc2@example.com"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>BCC (comma-separated)</FormLabel>
          <Input
            placeholder="bcc1@example.com, bcc2@example.com"
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
          />
        </FormControl>

        <Button
          type="submit"
          colorScheme="blue"
          isLoading={loading}
          loadingText="Sending..."
          size="lg"
          mt={4}
        >
          Send Email
        </Button>
      </VStack>
    </Box>
  );
}
